/**
 * User hooks using TanStack Query (Admin only)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { userService } from '@/api'
import { useUIStore } from '@/stores/uiStore'
import { handleApiError } from '@/lib/apiErrorHandler'
import type { UserCreate, UserUpdate, UserResponse } from '@/types/api'

interface ListUsersParams {
  skip?: number
  limit?: number
  include_inactive?: boolean
}

/**
 * Query keys for users
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: ListUsersParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
}

/**
 * Hook to get list of users
 */
export function useUsers(params?: ListUsersParams) {
  return useQuery<UserResponse[]>({
    queryKey: userKeys.list(params || {}),
    queryFn: () => userService.getUsers(params),
  })
}

/**
 * Hook to get a single user
 */
export function useUser(id: string) {
  return useQuery<UserResponse>({
    queryKey: userKeys.detail(id),
    queryFn: () => userService.getUser(id),
    enabled: !!id,
  })
}

/**
 * Hook for creating a user
 */
export function useCreateUser() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: UserCreate) => userService.createUser(data),
    onSuccess: (data: UserResponse) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      addToast({
        title: 'User created',
        description: `${data.username} has been added.`,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to create user')
    },
  })
}

/**
 * Hook for updating a user
 */
export function useUpdateUser(id: string) {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (data: UserUpdate) => userService.updateUser(id, data),
    onSuccess: (data) => {
      queryClient.setQueryData(userKeys.detail(id), data)
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      addToast({
        title: 'User updated',
        description: 'Changes have been saved',
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to update user')
    },
  })
}

/**
 * Hook for resetting a user's password
 */
export function useResetPassword() {
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      userService.resetPassword(id, password),
    onSuccess: (data: UserResponse) => {
      addToast({
        title: 'Password reset',
        description: `Password updated for ${data.username}.`,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to reset password')
    },
  })
}

/**
 * Hook for deactivating a user
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (id: string) => userService.deactivateUser(id),
    onSuccess: (data) => {
      queryClient.setQueryData(userKeys.detail(data.id), data)
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      addToast({
        title: 'User deactivated',
        description: `${data.username} has been deactivated`,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to deactivate user')
    },
  })
}

/**
 * Hook for activating a user
 */
export function useActivateUser() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation({
    mutationFn: (id: string) => userService.activateUser(id),
    onSuccess: (data) => {
      queryClient.setQueryData(userKeys.detail(data.id), data)
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      addToast({
        title: 'User activated',
        description: `${data.username} has been activated`,
        variant: 'success',
      })
    },
    onError: (error: { detail?: string }) => {
      handleApiError(error, 'Failed to activate user')
    },
  })
}
